import { useEffect, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from "react";
import { safeNextPath } from "../shared/next-path";

/**
 * A deliberately tiny History-API router: the app has ~20 flat routes and does
 * not need a routing library. `navigate` pushes state; `Link` intercepts plain
 * left-clicks only, so cmd/ctrl-click still opens a new tab.
 */
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  window.addEventListener("popstate", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("popstate", listener);
  };
}

export function navigate(to: string, options: { replace?: boolean } = {}) {
  if (to === window.location.pathname + window.location.search) return;
  if (options.replace) window.history.replaceState(null, "", to);
  else window.history.pushState(null, "", to);
  listeners.forEach((listener) => listener());
  if (!to.includes("#")) window.scrollTo({ top: 0 });
}

export function usePath(): string {
  return useSyncExternalStore(subscribe, () => window.location.pathname, () => "/");
}

/** Matches `/app/leads/:id` style patterns. Returns params or null. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const patternParts = pattern.split("/").filter(Boolean);
  const pathParts = path.split("/").filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;
  const params: Record<string, string> = {};
  for (let index = 0; index < patternParts.length; index += 1) {
    const part = patternParts[index];
    if (part.startsWith(":")) params[part.slice(1)] = decodeURIComponent(pathParts[index]);
    else if (part !== pathParts[index]) return null;
  }
  return params;
}

export function Link({ href, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  function handle(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!href.startsWith("/") || rest.target === "_blank") return;
    event.preventDefault();
    navigate(href);
  }
  return <a href={href} onClick={handle} {...rest} />;
}

export function Redirect({ to }: { to: string }) {
  useEffect(() => {
    navigate(to, { replace: true });
  }, [to]);
  return null;
}

/** Reads `#token=…` from the URL fragment (tokens never travel to the server in a URL). */
export function fragmentParam(name: string): string | null {
  const params = new URLSearchParams(window.location.hash.slice(1));
  return params.get(name);
}

export function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = title ? `${title} · Thekedar` : "Thekedar — Hire AI employees who work while you sleep";
  }, [title]);
}

/** Where to go after signing in or up: the page's `?next=` path, if safe. */
export function nextPath(): string | null {
  return safeNextPath(new URLSearchParams(window.location.search).get("next"));
}

/** Keeps the current page's `?next=` on a link to another auth page. */
export function withNext(href: string): string {
  const next = nextPath();
  return next ? `${href}?next=${encodeURIComponent(next)}` : href;
}
